/**
 * Utility functions for managing lead generation status in localStorage
 * This allows lead generation completion/decline status to persist across page reloads
 * Lead generation persistence is controlled by the data_collection_frequency setting
 */

/**
 * Check if lead generation persistence is enabled based on frequency setting
 * @param {Object} config - The chatbot configuration object
 * @returns {boolean} Whether persistence is enabled for this chatbot
 */
export const getCurrentFrequencySetting = (config) => {
  // Try to get from localStorage (UI state) - now per chatbot
  try {
    if (config?.kb_id) {
      const uiState = localStorage.getItem(`leadgen-ui-frequency-${config.kb_id}`);
      if (uiState) {
        return uiState;
      }

     
      const oldGlobalSetting = localStorage.getItem('leadgen-ui-frequency');
      if (oldGlobalSetting) {
      
        localStorage.setItem(`leadgen-ui-frequency-${config.kb_id}`, oldGlobalSetting);
        // Clean up old global setting
        localStorage.removeItem('leadgen-ui-frequency');
        return oldGlobalSetting;
      }
    }
  } catch (error) {
    // Silent fallback
  }

  // Fallback to config value or default (matching UI default)
  const frequency = config?.data_collection_frequency || 'always';
  return frequency;
};

export const isLeadGenPersistenceEnabled = (config) => {
  if (!config) return false;

  const frequency = getCurrentFrequencySetting(config);

  return frequency === 'once';
};

/**
 * Get the localStorage key for a specific chatbot's lead generation status
 * @param {string} kbId - The knowledge base ID
 * @returns {string} The localStorage key
 */
export const getLeadGenStorageKey = (kbId) => `leadgen-status-${kbId}`;

/**
 * Get stored lead generation status from localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {Object} Object with completed and declined boolean flags
 */
export const getStoredLeadGenStatus = (kbId, configOrClientUserId = null) => {
  if (!kbId) return { completed: false, declined: false };

  let persistenceEnabled = false;

  // Check if the second parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    persistenceEnabled = isLeadGenPersistenceEnabled(configOrClientUserId);
  }
  // For backward compatibility with client user ID, always enable persistence
  // This maintains existing behavior for any legacy code
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceEnabled = true;
  }

  if (!persistenceEnabled) {
    return { completed: false, declined: false };
  }

  try {
    const stored = localStorage.getItem(getLeadGenStorageKey(kbId));
    return stored ? JSON.parse(stored) : { completed: false, declined: false };
  } catch (error) {
    return { completed: false, declined: false };
  }
};

/**
 * Store lead generation status to localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object} status - Object with completed and declined boolean flags
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const setStoredLeadGenStatus = (kbId, status, configOrClientUserId = null) => {
  if (!kbId) return;

  let persistenceEnabled = false;

  // Check if the third parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    const frequency = getCurrentFrequencySetting(configOrClientUserId);
    persistenceEnabled = isLeadGenPersistenceEnabled(configOrClientUserId);

    // If frequency is 'always', clear any existing storage and don't save
    if (frequency === 'always') {
      try {
        localStorage.removeItem(getLeadGenStorageKey(kbId));
      } catch (error) {
        // Silent fallback
      }
      return;
    }
  }
  // For backward compatibility with client user ID, always enable persistence
  // This maintains existing behavior for any legacy code
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceEnabled = true;
  }

  if (!persistenceEnabled) {
    return;
  }

  try {
    localStorage.setItem(getLeadGenStorageKey(kbId), JSON.stringify(status));
  } catch (error) {
    // Silent fallback
  }
};

/**
 * Clear stored lead generation status from localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const clearStoredLeadGenStatus = (kbId, configOrClientUserId = null) => {
  if (!kbId) return;

  let persistenceEnabled = false;

  // Check if the second parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    persistenceEnabled = isLeadGenPersistenceEnabled(configOrClientUserId);
  }
  // For backward compatibility with client user ID, always enable persistence
  // This maintains existing behavior for any legacy code
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceEnabled = true;
  }

  if (!persistenceEnabled) {
    return;
  }

  try {
    localStorage.removeItem(getLeadGenStorageKey(kbId));
  } catch (error) {
    // Silent fallback
  }
};

/**
 * Check if lead generation should be shown based on stored status and configuration
 * @param {string} kbId - The knowledge base ID
 * @param {Object} config - The chatbot configuration
 * @returns {boolean} Whether lead generation should be shown
 */
export const shouldShowLeadGen = (kbId, config) => {
  if (!config?.data_collection) return false;

  const storedStatus = getStoredLeadGenStatus(kbId, config);
  if (storedStatus.completed || storedStatus.declined) return false;

  return config.show_data_collection_on_conversation_begin;
};

/**
 * Reset lead generation status for a new session (optional utility)
 * This can be called when you want to allow lead generation to appear again
 * Note: By default, lead generation status persists across chat resets
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const resetLeadGenForNewSession = (kbId, configOrClientUserId = null) => {
  clearStoredLeadGenStatus(kbId, configOrClientUserId);
};

/**
 * Force clear lead generation status (for admin/debug purposes)
 * This completely removes the stored status, allowing lead gen to appear again
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const forceResetLeadGen = (kbId, configOrClientUserId = null) => {
  clearStoredLeadGenStatus(kbId, configOrClientUserId);
};

/**
 * Check if user has completed lead generation
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {boolean} Whether lead generation was completed
 */
export const hasCompletedLeadGen = (kbId, configOrClientUserId = null) => {
  const status = getStoredLeadGenStatus(kbId, configOrClientUserId);
  return status.completed;
};

/**
 * Check if user has declined lead generation
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {boolean} Whether lead generation was declined
 */
export const hasDeclinedLeadGen = (kbId, configOrClientUserId = null) => {
  const status = getStoredLeadGenStatus(kbId, configOrClientUserId);
  return status.declined;
};

/**
 * Clear lead generation storage when frequency is set to 'always'
 * This ensures no persistence when frequency is 'always'
 * @param {string} kbId - The knowledge base ID
 * @param {Object} config - The chatbot configuration
 */
export const clearStorageForAlwaysFrequency = (kbId, config) => {
  if (!kbId || !config) return;

  const frequency = getCurrentFrequencySetting(config);
  if (frequency === 'always') {
    try {
      localStorage.removeItem(getLeadGenStorageKey(kbId));
    } catch (error) {
    }
  }
};
